import { useMemo } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MediaCard from "@/components/feature/MediaCard";
import NewsListItem from "@/components/feature/NewsListItem";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { findDetail, formatDate, kindLabel } from "@/lib/content";
import type { ContentKind } from "@/types/media";

interface MediaEntry {
  id: string;
  title: string;
  year: number;
  cover: string;
  summary: string;
  rating: number;
  tags: string[];
}

interface NewsEntry {
  id: string;
  title: string;
  cover: string;
  summary: string;
  publishDate: string;
  source: string;
}

export default function Favorites() {
  const { favorites, toggle, clear } = useFavorites();

  useDocumentMeta({
    title: "我的收藏 - 夜无疆影视大全",
    description: "查看你在夜无疆收藏的电影、电视剧与影视资讯，收藏内容保存在本地浏览器中。",
    keywords: "我的收藏,影视收藏,夜无疆收藏",
  });

  const { media, news } = useMemo(() => {
    const mediaList: MediaEntry[] = [];
    const newsList: NewsEntry[] = [];

    favorites.forEach((favId) => {
      const detail = findDetail(favId);
      if (!detail) return;

      if (detail.news) {
        const article = detail.news;
        newsList.push({
          id: article.id,
          title: article.title,
          cover: article.cover,
          summary: article.summary,
          publishDate: formatDate(article.publishDate),
          source: article.source,
        });
        return;
      }

      const item = detail.movie ?? detail.tvshow;
      if (!item) return;
      mediaList.push({
        id: item.id,
        title: item.title,
        year: item.year,
        cover: item.cover,
        summary: item.summary,
        rating: item.rating,
        tags: [item.region, ...item.genres],
      });
    });

    return { media: mediaList, news: newsList };
  }, [favorites]);

  const total = media.length + news.length;

  const kindCount = useMemo(() => {
    const counts: Record<ContentKind, number> = { movie: 0, tvshow: 0, news: 0 };
    favorites.forEach((favId) => {
      const detail = findDetail(favId);
      if (detail) counts[detail.kind] += 1;
    });
    return counts;
  }, [favorites]);

  return (
    <div className="w-full animate-fadeIn">
      <div className="border-b border-background-200/70 bg-background-100/40 px-4 py-8 md:px-6 lg:px-10 lg:py-10">
        <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "我的收藏" }]} />
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">我的收藏</h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600">
              收藏的内容会保存在当前浏览器中，方便你随时回看。该功能为前端本地演示，不会上传至服务器。
            </p>
          </div>
          {total > 0 && (
            <button
              type="button"
              onClick={clear}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2.5 text-sm font-label text-foreground-700 transition-colors duration-200 hover:border-primary-500/60 hover:text-primary-400"
            >
              <i className="ri-delete-bin-6-line" />
              清空收藏
            </button>
          )}
        </div>

        {total > 0 && (
          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="rounded-md bg-background-200/70 px-3 py-1">共 {total} 项</span>
            <span className="rounded-md bg-background-200/70 px-3 py-1">电影 {kindCount.movie}</span>
            <span className="rounded-md bg-background-200/70 px-3 py-1">电视剧 {kindCount.tvshow}</span>
            <span className="rounded-md bg-background-200/70 px-3 py-1">资讯 {kindCount.news}</span>
          </div>
        )}
      </div>

      <div className="w-full px-4 py-8 md:px-6 lg:px-10 lg:py-10">
        {total === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-100/50 px-6 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/70 text-3xl text-foreground-500">
              <i className="ri-heart-3-line" />
            </span>
            <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-950">还没有收藏任何内容</h2>
            <p className="mt-2 max-w-md text-sm leading-7 text-foreground-600">
              在电影、电视剧或资讯详情页点击「收藏」，就能把它们保存在这里。
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/movies"
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label text-background-50 transition-colors duration-200 hover:bg-primary-600"
              >
                <i className="ri-film-line" />
                浏览电影
              </Link>
              <Link
                to="/tvshows"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-6 py-3 text-sm font-label text-foreground-800 transition-colors duration-200 hover:border-primary-500/60 hover:text-primary-400"
              >
                浏览电视剧
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-10">
            {media.length > 0 && (
              <section>
                <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">
                  收藏的影视作品（{media.length}）
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                  {media.map((item) => (
                    <div key={item.id} className="relative">
                      <MediaCard
                        id={item.id}
                        title={item.title}
                        year={item.year}
                        cover={item.cover}
                        summary={item.summary}
                        tags={item.tags}
                        rating={item.rating}
                      />
                      <button
                        type="button"
                        onClick={() => toggle(item.id)}
                        aria-label={`取消收藏 ${item.title}`}
                        className="absolute right-2 top-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-background-50/85 text-primary-400 transition-colors duration-200 hover:bg-primary-500 hover:text-background-50"
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {news.length > 0 && (
              <section>
                <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">收藏的资讯（{news.length}）</h2>
                <div className="space-y-4">
                  {news.map((item) => (
                    <div key={item.id} className="relative">
                      <NewsListItem
                        id={item.id}
                        title={item.title}
                        publishDate={item.publishDate}
                        source={item.source}
                        summary={item.summary}
                        cover={item.cover}
                      />
                      <button
                        type="button"
                        onClick={() => toggle(item.id)}
                        aria-label={`取消收藏 ${item.title}`}
                        className="absolute right-3 top-3 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-background-50/85 text-primary-400 transition-colors duration-200 hover:bg-primary-500 hover:text-background-50"
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <p className="text-xs text-foreground-500">
              提示：{kindLabel("movie")}、{kindLabel("tvshow")} 与 {kindLabel("news")} 均可收藏，点击右上角可快速移除。
            </p>
          </div>
        )}
      </div>
    </div>
  );
}